/**
 * MOXIE: Localizador de Tiendas MIC / LITTLE MIC
 *
 * Componente custom element que:
 * - Consume la API de tiendas con autenticación segura por headers
 * - Integra Google Maps con marcadores personalizados
 * - Soporta filtros por país, ciudad y búsqueda de texto
 * - Sincroniza con el selector de marca (MoxieVendor)
 *
 * @element store-locator
 * @see sections/mox-custom-store-locator.liquid
 */

// ─── Configuración de API ────────────────────────────────────────────────────
// ⚠️ SEGURIDAD: estas credenciales quedan expuestas en el bundle JS público —
// cualquier visitante puede leerlas en devtools y llamar la API directamente.
// PENDIENTE: mover el lookup de tiendas detrás de un App Proxy de Shopify
// (o un endpoint serverless) que inyecte el token server-side. El token debe
// rotarse una vez migrado, ya que su valor actual está comprometido.
const API_CREDENTIALS = {
  appKey: "mic-ecommerce",
  token: "B9216C825114EDEB51FE68471ECD81F4D6C09D19",
  user: "Ecommerce",
};

// ─── Constantes ──────────────────────────────────────────────────────────────

/** Marca usada cuando la sección no declara bloques */
const BRAND_FALLBACK = "movies";

/** Filtros por defecto de la consulta a la API > la sección los sobrescribe vía data-* */
const QUERY_DEFAULTS = {
  country: "colombia",
  storeType: "propio,sociedad,franquicia",
  status: "activa",
};

/** Centro y zoom de rescate si la sección no los configura (Colombia) */
const CENTER_FALLBACK = { lat: 4.5709, lng: -74.2973 };
const ZOOM_FALLBACK = 6;
const ZOOM_FOCUSED = 14;

/** Caché en memoria > evita llamadas repetidas para la misma consulta */
const _apiCache = new Map(); // key: url → { data: Store[], ts: number }
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

// ─── Promesa única para cargar Google Maps (compartida entre instancias) ──────
let _mapsLoadPromise = null;

// ─── Utilidades ──────────────────────────────────────────────────────────────

function deg2rad(d) {
  return d * (Math.PI / 180);
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// MOXIE: la API entrega las coordenadas como texto y con formatos mezclados:
/** @param {*} val @returns {number} */
function parseCoord(val) {
  let str = String(val == null ? "" : val).replace(/\s+/g, "");
  if (!str.includes(".")) str = str.replace(",", ".");
  return parseFloat(str);
}

/**
 * Coordenadas usables de una tienda, o null si vienen corruptas.
 * @param {*} store
 * @returns {{lat: number, lng: number}|null}
 */
function storeCoords(store) {
  const lat = parseCoord(store.latitude);
  const lng = -Math.abs(parseCoord(store.longitude));

  if (!isFinite(lat) || !isFinite(lng) || lat === 0 || lng === 0) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

  return { lat, lng };
}

function mapsLink(store) {
  const coords = storeCoords(store);
  if (coords) {
    return `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;
  }
  const query = encodeURIComponent(
    [store.adress, store.city, store.country].filter(Boolean).join(", "),
  );
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

function formatDist(km) {
  return km < 1 ? `${(km * 1000).toFixed(0)} m` : `${km.toFixed(1)} km`;
}

function toTitleCase(str) {
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Escapa texto para interpolación segura en HTML.
 * Los campos provienen de una API externa; sin escape hay vector de DOM XSS.
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Parsea un "lat, lng" escrito en el CMS.
 * @param {string|undefined} text
 * @returns {{lat: number, lng: number}|null} null si no son dos números válidos
 */
function parseCenter(text) {
  const [lat, lng] = String(text || "")
    .split(",")
    .map((n) => parseFloat(n));
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

/**
 * Arma la URL de consulta a la API a partir de un objeto plano de filtros.
 * @param {string} apiUrl
 * @param {Record<string, string>} query
 * @returns {string}
 */
function buildStoreUrl(apiUrl, query) {
  const entries = Object.entries(query).filter(
    ([, v]) => v != null && v !== "",
  );
  const qs = new URLSearchParams(entries).toString().replace(/%2C/g, ",");
  return `${apiUrl}/api/Store/GetStore?${qs}`;
}

// ─── Componente ──────────────────────────────────────────────────────────────

class StoreLocator extends HTMLElement {
  connectedCallback() {
    this.refs = this._collectRefs();

    this._apiUrl = (this.dataset.apiUrl || "").replace(/\/$/, "");
    this._mapsKey = this.dataset.mapsKey || "";
    this._accentColor = this.dataset.accent || "#1a1a2e";

    this._query = {
      country: this.dataset.country ?? QUERY_DEFAULTS.country,
      storeType: this.dataset.storeType ?? QUERY_DEFAULTS.storeType,
      status: this.dataset.status ?? QUERY_DEFAULTS.status,
    };

    /** @type {{vendor: string, brand: string, panelTitle: string}[]} */
    this._brands = [];
    try {
      this._brands = JSON.parse(this.dataset.brands || "[]");
    } catch {
      /* config inválida: se cae a marca única */
    }
    this._defaultBrand = this._brands[0]?.brand || BRAND_FALLBACK;

    this._center = parseCenter(this.dataset.mapCenter) || CENTER_FALLBACK;
    this._zoom = parseInt(this.dataset.mapZoom, 10) || ZOOM_FALLBACK;

    this._allStores = [];
    this._filtered = [];
    this._map = null;
    this._markers = new Map();
    this._infoWindow = null;
    this._activeId = null;
    this._userLoc = null;
    this._searchTimer = null;

    this._setupUiListeners();
    this._init();
  }

  disconnectedCallback() {
    clearTimeout(this._searchTimer);
    this._markers.forEach((m) => m.setMap(null));
  }

  /**
   * Reúne los elementos con atributo ref="nombre" dentro del componente.
   * Reemplaza a this.refs de @theme/component, que no existe en este tema.
   * @returns {Record<string, any>}
   */
  _collectRefs() {
    /** @type {Record<string, any>} */
    const refs = {};
    this.querySelectorAll("[ref]").forEach((el) => {
      refs[/** @type {string} */ (el.getAttribute("ref"))] = el;
    });
    return refs;
  }

  // ── Setup UI ──────────────────────────────────────────────────────────────

  _setupUiListeners() {
    this.refs.searchInput?.addEventListener("input", () => {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this._applyFilters(), 300);
    });

    this.refs.countryFilter?.addEventListener("change", () => {
      const cityFilter = /** @type {HTMLSelectElement} */ (
        this.refs.cityFilter
      );
      if (cityFilter) cityFilter.value = "";
      this._fetchStores();
    });
    this.refs.cityFilter?.addEventListener("change", () =>
      this._applyFilters(),
    );

    this.refs.resetBtn?.addEventListener("click", () => this._resetFilters());

    this.refs.retryBtn?.addEventListener("click", () => {
      if (this.refs.errorMessage) this.refs.errorMessage.hidden = true;
      this._fetchStores();
    });
  }

  // ── Inicialización ────────────────────────────────────────────────────────

  async _init() {
    this._showLoading(true);

    try {
      await this._loadGoogleMaps();
      this._initMap();
      await this._fetchStores();
      this._getUserLocation();
    } catch (err) {
      this._showError(err.message);
    } finally {
      this._showLoading(false);
    }
  }

  // ── Google Maps ───────────────────────────────────────────────────────────

  _loadGoogleMaps() {
    if (window.google?.maps) return Promise.resolve();
    if (_mapsLoadPromise) return _mapsLoadPromise;

    /** @type {Promise<void>} */
    _mapsLoadPromise = new Promise((resolve, reject) => {
      const cb = "__moxieStoreLocatorMapsReady";
      /** @type {ReturnType<typeof setTimeout>} */
      let timer;
      const win = /** @type {any} */ (window);

      win[cb] = () => {
        delete win[cb];
        clearTimeout(timer);
        resolve();
      };

      const script = document.createElement("script");
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this._mapsKey}&callback=${cb}&loading=async`;
      script.onerror = () => {
        _mapsLoadPromise = null;
        reject(
          new Error(
            "No se pudo cargar la API de Google Maps. Verifica la clave API.",
          ),
        );
      };

      document.head.appendChild(script);

      timer = setTimeout(() => {
        _mapsLoadPromise = null;
        reject(
          new Error("Timeout: Google Maps tardó demasiado en cargar (15s)."),
        );
      }, 15000);
    });

    return _mapsLoadPromise;
  }

  _initMap() {
    if (!window.google?.maps) return;

    this._map = new google.maps.Map(this.refs.mapContainer, {
      center: this._center,
      zoom: this._zoom,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "transit.station",
          stylers: [{ visibility: "simplified" }],
        },
      ],
    });

    this._infoWindow = new google.maps.InfoWindow({ maxWidth: 280 });

    google.maps.event.addListener(this._map, "click", () => {
      this._infoWindow.close();
      this._deactivateStore(true);
    });
  }

  // ── API de Tiendas ────────────────────────────────────────────────────────

  // MOXIE: el tema no tiene selector de marca (MoxieVendor), así que se
  // consultan todas las marcas de los bloques en paralelo y se unen.
  async _fetchStores() {
    const brands = [
      ...new Set(this._brands.map((b) => b.brand).filter(Boolean)),
    ];
    if (brands.length === 0) brands.push(this._defaultBrand);

    this._showLoading(true);

    try {
      const results = await Promise.all(
        brands.map((brand) => this._requestBrand(brand)),
      );

      // Una tienda puede venir en ambas marcas: se deja una sola vez
      const byId = new Map();
      results.flat().forEach((s) => {
        if (!byId.has(s.idRows)) byId.set(s.idRows, s);
      });
      const stores = [...byId.values()];

      this._markers.forEach((m) => m?.setMap(null));
      this._markers.clear();
      this._allStores = stores;
      this._populateCountryFilter(stores);
      this._updateCityFilter();
      this._applyFilters();
    } catch (err) {
      this._showError(err.message);
    } finally {
      this._showLoading(false);
    }
  }

  /**
   * Tiendas activas de una marca (con caché de 5 min).
   * @param {string} brand
   * @returns {Promise<any[]>}
   */
  async _requestBrand(brand) {
    const url = buildStoreUrl(this._apiUrl, {
      brand,
      country: this._queryCountry(),
      storeType: this._query.storeType,
      status: this._query.status,
    });

    const cached = _apiCache.get(url);
    if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "MIC-API-AppKey": API_CREDENTIALS.appKey,
        "MIC-API-Token": API_CREDENTIALS.token,
        "MIC-API-User": API_CREDENTIALS.user,
        "Content-Type": "application/json",
      },
      mode: "cors",
    });
    const rawText = await response.text();

    if (!response.ok) {
      throw new Error(
        `Error HTTP ${response.status}: ${response.statusText}. Respuesta: ${rawText.substring(0, 200)}`,
      );
    }

    let data;
    try {
      data = JSON.parse(rawText);
    } catch {
      throw new Error(
        `La API devolvió una respuesta que no es JSON válido.\nPrimeros 300 caracteres: ${rawText.substring(0, 300)}`,
      );
    }

    if (Array.isArray(data) && data.length > 0 && data[0]?.statusCode) {
      const errMsg =
        data[0].message || data[0].exeption || "Error desconocido de la API";
      throw new Error(
        `Error de la API (código ${data[0].statusCode}): ${errMsg}`,
      );
    }

    if (!Array.isArray(data)) {
      throw new Error(
        `Formato inesperado: se esperaba un arreglo, se recibió ${typeof data}.`,
      );
    }

    const activeStores = data.filter((s) => s.status !== "INACTIVA");
    _apiCache.set(url, { data: activeStores, ts: Date.now() });
    return activeStores;
  }

  /**
   * País que viaja en la query
   * @returns {string}
   */
  _queryCountry() {
    const selected = /** @type {HTMLSelectElement} */ (
      this.refs.countryFilter?.value || ""
    ).trim();
    return (selected || this._query.country).toLowerCase();
  }

  // ── Filtrado ──────────────────────────────────────────────────────────────

  _applyFilters() {
    const search = (this.refs.searchInput?.value || "").toLowerCase().trim();
    const country = (this.refs.countryFilter?.value || "").toUpperCase().trim();
    const city = (this.refs.cityFilter?.value || "").toUpperCase().trim();

    this._filtered = this._allStores.filter((store) => {
      if (country && store.country?.toUpperCase() !== country) return false;
      if (city && store.city?.toUpperCase() !== city) return false;
      if (search) {
        const haystack = [
          store.warehouseNameOriginal,
          store.warehouseName,
          store.adress,
          store.city,
          store.state,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });

    this._renderStoreList(this._filtered);
    this._renderMarkers(this._filtered);
    this._updateCount(this._filtered.length);
  }

  _resetFilters() {
    if (this.refs.searchInput) this.refs.searchInput.value = "";
    if (this.refs.countryFilter) this.refs.countryFilter.value = "";
    if (this.refs.cityFilter) this.refs.cityFilter.value = "";

    // Sin país seleccionado se vuelve al configurado en la sección
    this._fetchStores();

    if (this._map) {
      this._map.setCenter(this._center);
      this._map.setZoom(this._zoom);
    }

    this._deactivateStore(true);
  }

  // El select de país se construye con los países que devuelve la API
  /** @param {any[]} stores */
  _populateCountryFilter(stores) {
    const sel = /** @type {HTMLSelectElement} */ (this.refs.countryFilter);
    if (!sel) return;
    if (sel.value) return;

    const countries = [
      ...new Set(
        stores.map(/** @param {any} s */ (s) => s.country).filter(Boolean),
      ),
    ].sort();

    sel.innerHTML = '<option value="">País</option>';
    countries.forEach((country) => {
      const opt = document.createElement("option");
      opt.value = country;
      opt.textContent = toTitleCase(country);
      sel.appendChild(opt);
    });
  }

  _updateCityFilter() {
    const sel = /** @type {HTMLSelectElement} */ (this.refs.cityFilter);
    if (!sel) return;

    const country = /** @type {HTMLSelectElement} */ (
      this.refs.countryFilter?.value || ""
    )
      .toUpperCase()
      .trim();

    if (!country) {
      sel.innerHTML = '<option value="">Ciudad</option>';
      sel.disabled = true;
      return;
    }

    const storesInCountry = this._allStores.filter(
      /** @param {any} s */ (s) => s.country?.toUpperCase() === country,
    );
    this._populateCityFilter(storesInCountry);
    sel.disabled = false;
  }

  _populateCityFilter(stores) {
    const sel = /** @type {HTMLSelectElement} */ (this.refs.cityFilter);
    if (!sel) return;

    const prevValue = sel.value;
    const cities = [
      ...new Set(stores.map((s) => s.city).filter(Boolean)),
    ].sort();

    sel.innerHTML = '<option value="">Ciudad</option>';
    cities.forEach((city) => {
      const opt = document.createElement("option");
      opt.value = city;
      opt.textContent = toTitleCase(city);
      sel.appendChild(opt);
    });

    if (prevValue && cities.includes(prevValue)) sel.value = prevValue;
  }

  // ── Renderizado de lista ──────────────────────────────────────────────────

  _renderStoreList(stores) {
    const list = this.refs.storeList;
    if (!list) return;

    if (stores.length === 0) {
      list.innerHTML = "";
      const empty = document.createElement("div");
      empty.className = "sl-empty";
      empty.innerHTML = `
        <svg class="sl-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
        </svg>
        <p>No se encontraron tiendas con los filtros actuales.</p>
      `;
      const btn = document.createElement("button");
      btn.className = "sl-btn sl-btn--outline";
      btn.textContent = "Limpiar filtros";
      btn.addEventListener("click", () => this._resetFilters());
      empty.appendChild(btn);
      list.appendChild(empty);
      return;
    }

    const sorted = this._userLoc
      ? [...stores].sort((a, b) => {
          const ca = storeCoords(a);
          const cb = storeCoords(b);
          const da = ca
            ? haversineKm(this._userLoc.lat, this._userLoc.lng, ca.lat, ca.lng)
            : Infinity;
          const db = cb
            ? haversineKm(this._userLoc.lat, this._userLoc.lng, cb.lat, cb.lng)
            : Infinity;
          return da - db;
        })
      : stores;

    list.innerHTML = sorted.map((s) => this._cardHtml(s)).join("");

    list.querySelectorAll(".sl-card").forEach((card) => {
      const id = parseInt(card.dataset.id, 10);
      card.addEventListener("click", () => this._activateStore(id));
      card.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this._activateStore(id);
        }
      });
    });
  }

  _cardHtml(store) {
    const isActive = this._activeId === store.idRows;
    const mapsUrl = mapsLink(store);
    const coords = storeCoords(store);
    const name = escapeHtml(store.warehouseNameOriginal || store.warehouseName);

    let distHtml = "";
    if (this._userLoc && coords) {
      const km = haversineKm(
        this._userLoc.lat,
        this._userLoc.lng,
        coords.lat,
        coords.lng,
      );
      distHtml = `<span class="sl-card__dist">${formatDist(km)}</span>`;
    }

    const badge =
      store.format && store.format !== "POR DEFINIR" && store.format !== "MIXTO"
        ? `<span class="sl-card__badge">${escapeHtml(store.format)}</span>`
        : `<span class="sl-card__badge sl-card__badge--neutral">${escapeHtml(store.format || store.businessType || "")}</span>`;

    const phone =
      store.phoneNumber && store.phoneNumber !== "-"
        ? `<span class="sl-card__phone">
           <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
             <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.09 6.09l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 17z"/>
           </svg>
           ${escapeHtml(store.phoneNumber)}${store.contactExtension ? ` ext. ${escapeHtml(store.contactExtension)}` : ""}
         </span>`
        : "";

    return `
      <div
        class="sl-card${isActive ? " sl-card--active" : ""}"
        data-id="${escapeHtml(store.idRows)}"
        role="button"
        tabindex="0"
        aria-label="${name}"
      >
        <div class="sl-card__top">
          ${badge}
          ${distHtml}
        </div>
        <p class="sl-card__name">${name}</p>
        <p class="sl-card__addr">${escapeHtml(store.adress)}</p>
        <p class="sl-card__city">${escapeHtml(toTitleCase(store.city))}${store.state ? ", " + escapeHtml(toTitleCase(store.state)) : ""}</p>
        ${phone}
        <div class="sl-card__foot">
          <a
            href="${escapeHtml(mapsUrl)}"
            target="_blank"
            rel="noopener noreferrer"
            class="sl-card__glink"
            onclick="event.stopPropagation()"
            aria-label="Ver ${name} en Google Maps (abre en nueva pestaña)"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            Ver en Google Maps
          </a>
        </div>
      </div>
    `;
  }

  // ── Marcadores del mapa ───────────────────────────────────────────────────

  /** @param {any[]} stores */
  _renderMarkers(stores) {
    if (!this._map || !window.google?.maps) return;

    const visibleIds = new Set(
      stores
        .filter(/** @param {any} s */ (s) => storeCoords(s) != null)
        .map(/** @param {any} s */ (s) => s.idRows),
    );

    this._markers.forEach((marker, id) =>
      marker?.setVisible(visibleIds.has(id)),
    );

    stores.forEach(
      /** @param {any} store */ (store) => {
        if (this._markers.has(store.idRows)) return;
        const coords = storeCoords(store);
        if (!coords) return;

        const marker = new google.maps.Marker({
          position: coords,
          map: this._map,
          title: store.warehouseNameOriginal,
          icon: this._markerIcon(this._accentColor, false),
        });

        marker.addListener("click", () => this._activateStore(store.idRows));
        this._markers.set(store.idRows, marker);
      },
    );

    const visibleStores = stores.filter(
      /** @param {any} s */ (s) => visibleIds.has(s.idRows),
    );
    if (visibleStores.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      visibleStores.forEach(
        /** @param {any} s */ (s) =>
          bounds.extend(
            /** @type {{lat: number, lng: number}} */ (storeCoords(s)),
          ),
      );
      this._map.fitBounds(bounds, 50);
    } else if (visibleStores.length === 1) {
      const s = visibleStores[0];
      this._map.setCenter(
        /** @type {{lat: number, lng: number}} */ (storeCoords(s)),
      );
      this._map.setZoom(ZOOM_FOCUSED);
    } else {
      this._map.setCenter(this._center);
      this._map.setZoom(this._zoom);
    }
  }

  _markerIcon(fillColor, isActive) {
    const w = isActive ? 44 : 34;
    const h = isActive ? 55 : 43;
    const cx = isActive ? 22 : 17;
    const cy = isActive ? 22 : 17;
    const r = isActive ? 8 : 6.5;

    const svg = `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg">
      <path d="M${cx} 1C${cx - 11} 1 1 ${cy - 10} 1 ${cy}c0 ${Math.round(h * 0.38)} ${cx - 1} ${Math.round(h * 0.62)} ${cx - 1} ${Math.round(h * 0.62)}S${w - 1} ${cy + Math.round(h * 0.38)} ${w - 1} ${cy}C${w - 1} ${cy - 10} ${cx + 11} 1 ${cx} 1z"
        fill="${fillColor}" stroke="white" stroke-width="${isActive ? 2.5 : 2}"/>
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="white" fill-opacity="${isActive ? 1 : 0.9}"/>
    </svg>`;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      size: new google.maps.Size(w, h),
      anchor: new google.maps.Point(w / 2, h),
      scaledSize: new google.maps.Size(w, h),
    };
  }

  // ── Activación de tienda ──────────────────────────────────────────────────

  _activateStore(storeId) {
    const store = this._allStores.find((s) => s.idRows === storeId);
    if (!store) return;

    this._deactivateStore(false);
    this._activeId = storeId;

    this.refs.storeList?.querySelectorAll(".sl-card").forEach((el) => {
      el.classList.toggle(
        "sl-card--active",
        parseInt(el.dataset.id, 10) === storeId,
      );
    });

    const card = this.refs.storeList?.querySelector(
      `.sl-card[data-id="${storeId}"]`,
    );
    card?.scrollIntoView({ behavior: "smooth", block: "nearest" });

    this._markers.forEach((marker, id) => {
      const active = id === storeId;
      marker.setIcon(
        this._markerIcon(active ? "#e74c3c" : this._accentColor, active),
      );
      marker.setZIndex(active ? 1000 : 1);
    });

    const marker = this._markers.get(storeId);
    if (marker && this._map) {
      this._map.panTo(marker.getPosition());
      this._map.setZoom(ZOOM_FOCUSED);
      this._infoWindow.setContent(this._infoWindowHtml(store));
      this._infoWindow.open(this._map, marker);
    }
  }

  _deactivateStore(resetMarkers) {
    if (!this._activeId) return;

    this.refs.storeList
      ?.querySelector(".sl-card--active")
      ?.classList.remove("sl-card--active");
    this._infoWindow?.close();

    if (resetMarkers) {
      this._markers.forEach((m) => {
        m.setIcon(this._markerIcon(this._accentColor, false));
        m.setZIndex(1);
      });
    }

    this._activeId = null;
  }

  _infoWindowHtml(store) {
    const url = mapsLink(store);
    return `
      <div style="font-family:inherit;min-width:200px;max-width:270px;padding:4px 2px">
        <strong style="display:block;font-size:13px;font-weight:700;margin-bottom:5px;line-height:1.3">${escapeHtml(store.warehouseNameOriginal || store.warehouseName)}</strong>
        <p style="margin:0 0 2px;font-size:12px;color:#555">${escapeHtml(store.adress)}</p>
        <p style="margin:0 0 6px;font-size:12px;color:#555">${escapeHtml(toTitleCase(store.city))}, ${escapeHtml(toTitleCase(store.state || ""))}</p>
        ${
          store.phoneNumber && store.phoneNumber !== "-"
            ? `<p style="margin:0 0 8px;font-size:12px">📞 ${escapeHtml(store.phoneNumber)}</p>`
            : ""
        }
        <a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer"
           style="font-size:12px;color:#1a73e8;font-weight:600;text-decoration:none">
          Ver en Google Maps ↗
        </a>
      </div>
    `;
  }

  // ── Geolocalización ───────────────────────────────────────────────────────

  _getUserLocation() {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this._userLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this._renderStoreList(this._filtered);

        if (this._map && window.google?.maps) {
          new google.maps.Marker({
            position: this._userLoc,
            map: this._map,
            title: "Tu ubicación",
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 9,
              fillColor: "#4285F4",
              fillOpacity: 1,
              strokeWeight: 2.5,
              strokeColor: "#ffffff",
            },
            zIndex: 2000,
          });
        }
      },
      () => {
        /* geolocalización no disponible o denegada */
      },
      { timeout: 10000, maximumAge: 60000 },
    );
  }

  // ── UI Helpers ────────────────────────────────────────────────────────────

  _showLoading(show) {
    if (this.refs.loadingOverlay) this.refs.loadingOverlay.hidden = !show;
    if (this.refs.storeList)
      this.refs.storeList.style.opacity = show ? "0.35" : "";
  }

  _showError(msg) {
    if (!this.refs.errorMessage) return;
    this.refs.errorMessage.hidden = false;
    if (this.refs.errorText) {
      const clean = msg.length > 200 ? msg.substring(0, 200) + "..." : msg;
      this.refs.errorText.textContent = clean;
    }
  }

  _updateCount(n) {
    if (this.refs.storeCount) this.refs.storeCount.textContent = n;
  }
}

if (!customElements.get("store-locator")) {
  customElements.define("store-locator", StoreLocator);
}
